import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "wouter";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  RotateCw,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { redactSensitive } from "@/lib/redact-sensitive";
import { ApiError, publicApi } from "@/services/api-client";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { record, rows, type ApiRecord } from "@/hooks/use-api-resource";

export { record, rows };
export type { ApiRecord };

/** Read the first non-empty value among candidate keys (dot paths allowed). */
export function pick(source: unknown, ...keys: string[]): unknown {
  for (const key of keys) {
    let value: unknown = source;
    for (const part of key.split(".")) value = record(value)[part];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}
export const text = (source: unknown, ...keys: string[]): string => {
  const v = pick(source, ...keys);
  return v === undefined ? "" : typeof v === "object" ? "" : String(v);
};

/** Positive decimal string, comma accepted and normalised. */
export function normalizeAmount(raw: string): string | null {
  const v = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^\d+(\.\d{1,8})?$/.test(v)) return null;
  if (Number(v) <= 0) return null;
  return v.replace(/^0+(?=\d)/, "");
}

/** Only real http(s) URLs may be rendered as links. */
export function safeUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const u = new URL(value);
    return u.protocol === "https:" || u.protocol === "http:" ? u.href : null;
  } catch {
    return null;
  }
}

export function fmtWhen(value: unknown): string {
  if (!value) return "—";
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtAmount(amount: unknown, currency: unknown): string {
  if (amount === undefined || amount === null || amount === "") return "—";
  const n = Number(amount);
  const cur = currency ? ` ${String(currency)}` : "";
  if (!Number.isFinite(n)) return `${String(amount)}${cur}`;
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 8 })}${cur}`;
}

export function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "La requête a échoué.";
}
export const isAbort = (e: unknown) =>
  e instanceof DOMException && e.name === "AbortError";

/** Financial gate: business lock and the main context flag (never assumed open). */
export function useFinanceGate() {
  const d = useDashboard();
  const ext = d as unknown as { financialAllowed?: boolean };
  const allowed = !d.locked && !d.loading && ext.financialAllowed !== false;
  return { allowed, locked: d.locked, loading: d.loading, d };
}

/**
 * Server resource with request abort on supersede. No cache, no polling.
 * Pass null to skip the request entirely.
 */
export function useServer<T = unknown>(path: string | null) {
  const [state, setState] = useState<{
    data?: T;
    meta?: ApiRecord;
    loading: boolean;
    error: unknown;
  }>({ loading: Boolean(path), error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setState({ loading: false, error: null });
      return;
    }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    publicApi<T>(path, { signal: ctrl.signal })
      .then(({ data, meta }) =>
        setState({ data, meta: record(meta), loading: false, error: null }),
      )
      .catch((error) => {
        if (ctrl.signal.aborted || isAbort(error)) return;
        setState({ loading: false, error });
      });
    return () => ctrl.abort();
  }, [path, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

export function buildQuery(
  path: string,
  params: Record<string, string | number | undefined>,
) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "" && v !== "all") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

export interface PageInfo {
  page: number;
  last: number | null;
  total: number | null;
  hasNext: boolean;
}
/** Reads paginator data from either the data envelope or meta. */
export function pageInfo(
  data: unknown,
  meta: unknown,
  count: number,
  perPage: number,
): PageInfo {
  const src = [
    record(data),
    record(record(data).meta),
    record(meta),
    record(record(meta).pagination),
  ];
  const get = (...k: string[]) => {
    for (const s of src) {
      const v = pick(s, ...k);
      if (v !== undefined && Number.isFinite(Number(v))) return Number(v);
    }
    return null;
  };
  const page = get("current_page", "page") ?? 1;
  const last = get("last_page", "total_pages");
  const total = get("total");
  const nextUrl = src.some((s) => s.next_page_url || record(s.links).next);
  const hasNext = last !== null ? page < last : nextUrl || count >= perPage;
  return { page, last, total, hasNext };
}

export function Pager({
  info,
  onPage,
  loading,
}: {
  info: PageInfo;
  onPage: (p: number) => void;
  loading?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between gap-3 border-t px-4 py-3 text-xs text-muted-foreground"
      data-testid="pager"
    >
      <span>
        Page <b className="text-foreground">{info.page}</b>
        {info.last ? ` / ${info.last}` : ""}
        {info.total !== null
          ? ` · ${info.total} élément(s) au total (serveur)`
          : ""}
      </span>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={loading || info.page <= 1}
          onClick={() => onPage(info.page - 1)}
          aria-label="Page précédente"
          data-testid="button-page-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={loading || !info.hasNext}
          onClick={() => onPage(info.page + 1)}
          aria-label="Page suivante"
          data-testid="button-page-next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function ErrorBox({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  if (!error) return null;
  const kyb =
    error instanceof ApiError &&
    (error.code === "KYB_REQUIRED" || error.code === "KYC_REQUIRED");
  const cid = error instanceof ApiError ? error.correlationId : undefined;
  return (
    <div
      role="alert"
      data-testid="error-box"
      className={cn(
        "flex flex-col gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 sm:flex-row sm:items-center",
        className,
      )}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-semibold">{errorText(error)}</p>
        {error instanceof ApiError && (
          <p className="mt-0.5 font-mono text-[10px] opacity-75">
            {error.code}
            {cid ? ` · ${cid}` : ""}
          </p>
        )}
      </div>
      {kyb && (
        <Link
          href="/verification"
          className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 font-semibold text-primary-foreground"
        >
          <ShieldAlert className="h-3.5 w-3.5" /> Vérification
        </Link>
      )}
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 bg-card"
          onClick={onRetry}
          data-testid="button-retry"
        >
          <RotateCw className="mr-1 h-3.5 w-3.5" /> Réessayer
        </Button>
      )}
    </div>
  );
}

export function SkeletonRows({ n = 5 }: { n?: number }) {
  return (
    <div className="space-y-2 p-4" aria-busy="true" aria-label="Chargement">
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="h-11 animate-pulse rounded-lg bg-muted/70"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

export function Empty({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div
      className="flex flex-col items-center gap-3 px-6 py-14 text-center"
      data-testid="empty-state"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-primary">
        {icon}
      </span>
      <p className="font-bold">{title}</p>
      {children && (
        <div className="max-w-sm text-sm text-muted-foreground">{children}</div>
      )}
    </div>
  );
}

const TONES: Record<string, string> = {
  ACTIVE: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  COMPLETED: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  SUCCESS: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  APPROVED: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  CAPTURED: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  DELIVERED: "bg-emerald-50 text-emerald-700 ring-emerald-600/15",
  READ: "bg-slate-100 text-slate-600 ring-slate-500/15",
  LIVE: "bg-emerald-50 text-emerald-800 ring-emerald-600/25",
  SANDBOX: "bg-sky-50 text-sky-800 ring-sky-600/20",
  PENDING: "bg-amber-50 text-amber-700 ring-amber-600/20",
  INVITED: "bg-amber-50 text-amber-700 ring-amber-600/20",
  UNREAD: "bg-blue-50 text-blue-700 ring-blue-600/15",
  HELD: "bg-blue-50 text-blue-700 ring-blue-600/15",
  PROCESSING: "bg-blue-50 text-blue-700 ring-blue-600/15",
  FAILED: "bg-rose-50 text-rose-700 ring-rose-600/15",
  REJECTED: "bg-rose-50 text-rose-700 ring-rose-600/15",
  SUSPENDED: "bg-rose-50 text-rose-700 ring-rose-600/15",
  DISABLED: "bg-slate-100 text-slate-600 ring-slate-500/15",
  EXPIRED: "bg-slate-100 text-slate-600 ring-slate-500/15",
  RELEASED: "bg-slate-100 text-slate-600 ring-slate-500/15",
  REVOKED: "bg-slate-100 text-slate-600 ring-slate-500/15",
};
export function Tag({ value }: { value: unknown }) {
  const v = String(value ?? "—").toUpperCase();
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
        TONES[v] ?? "bg-muted text-muted-foreground ring-border",
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {v}
    </span>
  );
}

const SENSITIVE = /secret|token|password|signature|pan|cvv|pin/i;
/** Generic, read-only rendering of a server object (secrets masked). */
export function KV({
  data,
  exclude = [],
}: {
  data: unknown;
  exclude?: string[];
}) {
  const entries = Object.entries(record(data)).filter(
    ([k]) => !exclude.includes(k),
  );
  if (!entries.length)
    return (
      <p className="text-xs text-muted-foreground">Aucune donnée retournée.</p>
    );
  return (
    <dl className="grid grid-cols-[minmax(110px,auto)_1fr] gap-x-4 gap-y-1.5 text-[13px]">
      {entries.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="truncate font-mono text-[11px] text-muted-foreground">
            {k}
          </dt>
          <dd className="min-w-0 break-words text-right font-medium">
            {SENSITIVE.test(k) && typeof v === "string" ? (
              "••••••"
            ) : v === null || v === undefined ? (
              <span className="text-muted-foreground">—</span>
            ) : typeof v === "object" ? (
              <pre className="max-h-40 overflow-auto rounded bg-muted/60 p-2 text-left font-mono text-[10px]">
                {JSON.stringify(redactSensitive(v), null, 2)}
              </pre>
            ) : typeof v === "boolean" ? (
              v ? (
                "oui"
              ) : (
                "non"
              )
            ) : (
              String(v)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Explicit confirmation step for any money/destructive action. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirmer",
  destructive,
  busy,
  error,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <ErrorBox error={error} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={busy}
            data-testid="button-confirm-action"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Once-only secret display. Masked by default, cleared when the window loses
 * focus, the tab is hidden or the component unmounts. Never logged or stored.
 */
export function SecretOnce({
  label,
  value,
  onClear,
}: {
  label: string;
  value: string;
  onClear: () => void;
}) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const clearRef = useRef(onClear);
  clearRef.current = onClear;
  useEffect(() => {
    const hide = () => {
      setShown(false);
      clearRef.current();
    };
    const vis = () => document.hidden && clearRef.current();
    window.addEventListener("blur", hide);
    document.addEventListener("visibilitychange", vis);
    return () => {
      window.removeEventListener("blur", hide);
      document.removeEventListener("visibilitychange", vis);
    };
  }, []);
  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50/70 p-3"
      data-testid="secret-once"
    >
      <p className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
        {label}
      </p>
      <p className="mt-0.5 text-[11px] text-amber-900/80">
        Affiché une seule fois. Il sera effacé à la fermeture ou si l'onglet est
        masqué.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-card px-2 py-1.5 font-mono text-xs">
          {shown ? value : "•".repeat(Math.min(32, value.length || 12))}
        </code>
        <Button
          size="sm"
          variant="outline"
          className="h-8 bg-card"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? "Masquer" : "Révéler"}
          data-testid="button-reveal-secret"
        >
          {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 bg-card"
          aria-label="Copier"
          data-testid="button-copy-secret"
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
      {copied && (
        <p className="mt-1 text-[11px] font-semibold text-emerald-700">
          Copié dans le presse-papiers.
        </p>
      )}
    </div>
  );
}

export function Seg<T extends string>({
  value,
  onChange,
  items,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  items: [T, string][];
  label: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="inline-flex flex-wrap gap-1 rounded-lg border bg-card p-1"
    >
      {items.map(([v, l]) => (
        <button
          key={v}
          role="tab"
          aria-selected={value === v}
          onClick={() => onChange(v)}
          data-testid={`tab-${v}`}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-semibold transition",
            value === v
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:bg-accent",
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export const fieldCls =
  "h-9 w-full rounded-md border bg-card px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";
